import React, { useState, useEffect, useMemo } from 'react';
import { ordersApi, orgApi } from '../api';
import { usePermissions } from '../contexts/PermissionContext';
import { useAccount } from '../contexts/AccountContext';
import { useOfflineOperations } from '../hooks/useOfflineOperations';
import { generatePublicInvoiceUrl } from '../utils/invoiceEncoding';
import { invoiceImageService } from '../services/InvoiceImageService';
import {
  buildPrintableInvoiceFromOrder,
  printableInvoiceToLegacyInvoiceData,
} from '../services/printer';
import { usePrinterService } from '../hooks/usePrinterService';
import { getTimeframe } from '../timeframe';
import PermissionAction from './PermissionAction';
import BillingEditor from './BillingEditor';
import { useOfflineCollection } from '../hooks/useOfflineCollection';
import { ENTITY_TYPES, SYNC_STATUS } from '../db';
import { dataService } from '../services/dataService';
import { syncService } from '../services/sync/syncService';
import { showToast, showConfirm } from '../utils/toast';

const Billing = ({ user, subStatus, onSubscribe }) => {
  const { hasPermission } = usePermissions();
  const { selectedAccountId } = useAccount();
  const { canPerformOperation, showOfflineWarning, message: offlineMessage } = useOfflineOperations('billing');
  const { printInvoice } = usePrinterService();
  
  const isNetworkError = (error) => {
    if (!error) return false;
    const message = error.message || error.toString();
    return message.includes('Failed to fetch') || message.includes('NetworkError') || (message.includes('request') && message.includes('failed'));
  };
  
  // State
  const [billingOrders, setBillingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [org, setOrg] = useState(null);

  const {
    data: offlineOrders,
    loading: offlineLoading,
    hydrating,
    refresh: refreshOrders
  } = useOfflineCollection({
    entityType: ENTITY_TYPES.ORDER,
    fetcher: () => ordersApi.list({ accountId: selectedAccountId }),
    orgId: user?.orgId,
    accountId: selectedAccountId,
    enabled: !!selectedAccountId
  });

  useEffect(() => {
    if (!selectedAccountId) {
      setBillingOrders([]);
      setLoading(false);
      return;
    }

    const billingOnly = (offlineOrders || []).filter(order => order?.billing === true);
    setBillingOrders(billingOnly);
    setLoading(offlineLoading || hydrating);
  }, [offlineOrders, offlineLoading, hydrating, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId) return;
    (async () => {
      try {
        const orgData = await orgApi.get();
        setOrg(orgData);
      } catch (error) {
        console.error('Error loading org data:', error);
      }
    })();
  }, [selectedAccountId]);

  // Start editing an order
  const startEdit = (order) => {
    setEditingOrder(order);
    setShowEditModal(true);
  };

  // Handle save from editor
  const handleSave = async () => {
    await refreshOrders({ force: true });
    setShowEditModal(false);
    setEditingOrder(null);
  };

  // Handle cancel from editor
  const handleCancel = () => {
    setShowEditModal(false);
    setEditingOrder(null);
  };

  // Delete order
  const deleteOrder = async (orderId) => {
    const confirmed = await showConfirm({
      title: 'Delete billing order?',
      message: 'Are you sure you want to delete this billing order?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      destructive: true,
      variant: 'warning',
    });
    if (!confirmed) {
      return;
    }

    let localRecord = null;
    try {
      localRecord = await dataService.getEntity(ENTITY_TYPES.ORDER, orderId);
    } catch (err) {
      console.error('Failed to load local order before delete', err);
    }

    if (localRecord?.syncStatus === SYNC_STATUS.PENDING_CREATE) {
      try {
        await syncService.cancelQueuedCreate(ENTITY_TYPES.ORDER, orderId);
      } catch (queueErr) {
        console.error('Failed to cancel queued create', queueErr);
      }
      try {
        await dataService.removeEntity(ENTITY_TYPES.ORDER, orderId);
      } catch (removeErr) {
        console.error('Failed to remove local pending order', removeErr);
      }
      setBillingOrders((prev) => prev.filter(order => order.orderId !== orderId));
      showToast({ message: 'Offline bill removed before syncing.', variant: 'success' });
      return;
    }

    const markPendingDeleteLocally = async (record) => {
      try {
        const targetRecord = record || (await dataService.getEntity(ENTITY_TYPES.ORDER, orderId));
        if (targetRecord) {
          const updatedRecord = {
            ...targetRecord,
            syncStatus: SYNC_STATUS.PENDING_DELETE,
            pendingDelete: true
          };
          await dataService.upsertLocal(ENTITY_TYPES.ORDER, updatedRecord, { syncStatus: SYNC_STATUS.PENDING_DELETE });
          setBillingOrders((prev) => prev.map(order => order.orderId === orderId ? { ...order, syncStatus: SYNC_STATUS.PENDING_DELETE, pendingDelete: true } : order));
        } else {
          setBillingOrders((prev) => prev.filter(order => order.orderId !== orderId));
        }
      } catch (localError) {
        console.error('Failed to update local delete state', localError);
      }
    };

    try {
      if (!navigator.onLine) {
        await syncService.queueDelete(ENTITY_TYPES.ORDER, orderId);
        await markPendingDeleteLocally(localRecord);
        showToast({ message: 'Delete queued offline. It will sync when you are back online.', variant: 'success' });
        return;
      }

      await ordersApi.remove(orderId);
      await dataService.removeEntity(ENTITY_TYPES.ORDER, orderId);
      setBillingOrders((prev) => prev.filter(order => order.orderId !== orderId));
      await refreshOrders({ force: true });
      showToast({ message: 'Billing order deleted successfully.', variant: 'success' });
    } catch (error) {
      if (isNetworkError(error)) {
        await syncService.queueDelete(ENTITY_TYPES.ORDER, orderId);
        await markPendingDeleteLocally(localRecord);
        showToast({ message: 'Delete queued offline. It will sync when you are back online.', variant: 'success' });
      } else {
        console.error('Error deleting order:', error);
        showToast({ message: 'Failed to delete billing order. Please try again.', variant: 'error' });
      }
    }
  };


  // Copy invoice link to clipboard
  const copyInvoiceLink = async (orderId) => {
    try {
      const invoiceUrl = generatePublicInvoiceUrl(orderId);
      await navigator.clipboard.writeText(invoiceUrl);
      showToast({ message: 'Invoice link copied to clipboard!', variant: 'success' });
    } catch (error) {
      console.error('Error copying link:', error);
      showToast({ message: 'Failed to copy invoice link.', variant: 'error' });
    }
  };

  // Share invoice via WhatsApp
  const shareInvoice = async (order) => {
    if (!order.customerPhone) {
      showToast({ message: 'Customer phone number is required to share invoice', variant: 'warning' });
      return;
    }

    try {
      const printableInvoice = buildPrintableInvoiceFromOrder(order, org, {
        metadata: {
          source: 'billing-screen',
          extras: { trigger: 'billing.shareInvoice' },
        },
      });
      const invoiceData = printableInvoiceToLegacyInvoiceData(printableInvoice);

      // Send via WhatsApp
      await invoiceImageService.sendInvoiceViaWhatsApp(
        order.customerPhone,
        invoiceData,
        org,
        { includeImage: false } // Send only link for better WhatsApp preview
      );
      showToast({ message: 'Invoice shared via WhatsApp!', variant: 'success' });
    } catch (error) {
      console.error('Error sharing invoice:', error);
      showToast({ message: 'Failed to share invoice. Please try again.', variant: 'error' });
    }
  };

  // Print invoice to thermal printer
  const printInvoiceToPrinter = async (order) => {
    try {
      const printableInvoice = buildPrintableInvoiceFromOrder(order, org, {
        metadata: {
          source: 'billing',
          extras: { trigger: 'billing.printInvoice' },
        },
      });

      const result = await printInvoice(printableInvoice, {
        allowQueue: true,
        encoder: { autoCut: false },
      });

      if (result?.remote) {
        showToast({
          message: 'Print job sent to desktop printer.',
          variant: 'success',
        });
      } else if (result?.queued) {
        showToast({
          message: result?.remoteFallback
            ? 'Desktop printer is offline. Print job saved to local queue.'
            : 'Printer not connected. Print job saved to printer queue.',
          variant: 'warning',
        });
      } else {
        showToast({
          message: 'Print job sent to thermal printer.',
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Failed to print invoice:', error);
      if (error?.code === 'REMOTE_PRINT_FAILED') {
        showToast({
          message: error.message || 'Desktop printer is unavailable right now.',
          variant: 'error',
        });
        return;
      }
      showToast({
        message: 'Failed to print invoice. Check printer setup.',
        variant: 'error',
      });
    }
  };

  // Filter orders based on search term and date range
  const filteredOrders = billingOrders.filter(order => {
    const matchesSearch = order.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Time filter logic
    const orderDate = order.date || order.createdAt;
    let matchesFrom = true;
    let matchesTo = true;
    
    if (filterFrom && orderDate) {
      try {
        let dateObj;
        if (orderDate && typeof orderDate === 'object' && orderDate.seconds) {
          dateObj = new Date(orderDate.seconds * 1000);
        } else if (orderDate && typeof orderDate === 'object' && orderDate._seconds) {
          dateObj = new Date(orderDate._seconds * 1000);
        } else {
          dateObj = new Date(orderDate);
        }
        const fromDate = new Date(filterFrom);
        matchesFrom = dateObj >= fromDate;
      } catch (e) {
        matchesFrom = true;
      }
    }
    
    if (filterTo && orderDate) {
      try {
        let dateObj;
        if (orderDate && typeof orderDate === 'object' && orderDate.seconds) {
          dateObj = new Date(orderDate.seconds * 1000);
        } else if (orderDate && typeof orderDate === 'object' && orderDate._seconds) {
          dateObj = new Date(orderDate._seconds * 1000);
        } else {
          dateObj = new Date(orderDate);
        }
        const toDate = new Date(filterTo);
        toDate.setHours(23, 59, 59, 999); // Include the entire day
        matchesTo = dateObj <= toDate;
      } catch (e) {
        matchesTo = true;
      }
    }
    
    return matchesSearch && matchesFrom && matchesTo;
  });

  if (!hasPermission('orders:view')) {
    return <PermissionAction action="orders:view" onSubscribe={onSubscribe} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading billing orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-green-100 px-4 py-3">
        <h1 className="text-lg font-semibold text-gray-800">📄 Billing Orders</h1>
        <p className="text-sm text-gray-600">Manage and edit your billing invoices</p>
      </div>

      {/* Offline Warning */}
      {showOfflineWarning && (
        <div className="mx-4 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">{offlineMessage}</p>
        </div>
      )}

      <div className="p-4">
        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="min-w-0">
              <div className="text-[11px] text-gray-600 mb-1 h-[14px]">Search</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                <input
                  type="text"
                  placeholder="Search billing orders..."
                  className="w-full border rounded-full pl-8 pr-3 py-2 text-sm bg-green-50/60 focus:bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-600 mb-1 h-[14px]">From</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📅</span>
                <input
                  type="date"
                  className="w-full border rounded-full pl-8 pr-3 py-2 text-sm bg-green-50/60 focus:bg-white"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-600 mb-1 h-[14px]">To</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📅</span>
                <input
                  type="date"
                  className="w-full border rounded-full pl-8 pr-3 py-2 text-sm bg-green-50/60 focus:bg-white"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                />
              </div>
            </div>
            <div className="sm:col-span-2 md:col-span-1 min-w-0 flex flex-col justify-end">
              <div className="text-[11px] text-transparent mb-1 h-[14px]">&nbsp;</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="w-full sm:w-auto text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full px-3 py-2"
                  onClick={() => { 
                    setSearchTerm(""); 
                    setFilterFrom(""); 
                    setFilterTo(""); 
                  }}
                  title="Clear filters"
                >
                  <span className="mr-1">✕</span>Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Range Display */}
        <div className="mb-4">
          {(() => { 
            try { 
              const tf = getTimeframe(); 
              const fromDisp = filterFrom || tf.from; 
              const toDisp = filterTo || tf.to; 
              return (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 shadow-sm text-[11px] text-gray-700">
                  <span>Range:</span>
                  <span className="font-semibold">{fromDisp} → {toDisp}</span>
                </div>
              ); 
            } catch { 
              return null; 
            } 
          })()}
        </div>

        {/* Orders List */}
        <div className="space-y-3">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📄</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No billing orders found</h3>
              <p className="text-gray-600">Create your first billing order using Fast Billing</p>
            </div>
          ) : (
            filteredOrders.map((order) => {
              const isPendingSync = order.syncStatus && order.syncStatus !== SYNC_STATUS.SYNCED;
              const isPendingDelete = order.syncStatus === SYNC_STATUS.PENDING_DELETE || order.pendingDelete;
              const statusLabel = isPendingDelete ? '🗑️ Delete queued' : '⏳ Not synced yet';

              return (
                <div
                  key={order.orderId}
                  className={`bg-white rounded-lg border p-4 shadow-sm ${isPendingSync ? 'border-yellow-300 bg-yellow-50/40' : 'border-green-100'}`}
                >
                {/* Order Header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{order.name}</h3>
                    <p className="text-sm text-gray-600">{order.customerName}</p>
                    <p className="text-xs text-gray-500">
                      {(order.date?._seconds) ? new Date(order.date._seconds * 1000).toLocaleDateString() : new Date(order.date).toLocaleDateString()}
                    </p>
                    {isPendingSync && (
                      <span className="mt-2 inline-flex items-center gap-1 text-xs text-yellow-800 bg-yellow-200 px-2 py-0.5 rounded-full">
                        {statusLabel}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">₹{order.sellingPrice?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-gray-500">{order.items?.length || 0} items</p>
                  </div>
                </div>

                {/* Items Preview */}
                {order.items && order.items.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-gray-500 mb-1">Items:</div>
                    <div className="space-y-1">
                      {order.items.slice(0, 3).map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-700">{item.name}</span>
                          <span className="text-gray-600">₹{item.price} × {item.quantity}</span>
                        </div>
                      ))}
                      {order.items.length > 3 && (
                        <div className="text-xs text-gray-500">
                          ... and {order.items.length - 3} more items
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => startEdit(order)}
                    disabled={isPendingSync}
                    className={`flex-1 min-w-[80px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isPendingSync ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                  >
                    ✏️ {isPendingDelete ? 'Delete queued' : (isPendingSync ? 'Edit (pending sync)' : 'Edit')}
                  </button>
                  <button
                    onClick={() => printInvoiceToPrinter(order)}
                    disabled={isPendingSync}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isPendingSync ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-white text-green-600 border border-green-300 hover:bg-green-50'}`}
                    title="Print invoice to thermal printer"
                  >
                    🖨️ Print
                  </button>
                  <button
                    onClick={() => shareInvoice(order)}
                    disabled={!order.customerPhone || isPendingSync}
                    className={`flex-1 min-w-[80px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isPendingSync ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-green-500 text-white hover:bg-green-600'} ${!order.customerPhone ? 'opacity-50' : ''}`}
                    title={isPendingSync ? (isPendingDelete ? 'Deletion queued while offline' : 'Sync order online to share') : (order.customerPhone ? 'Share invoice via WhatsApp' : 'Customer phone number required')}
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                    Share
                  </button>
                  <button
                    onClick={() => copyInvoiceLink(order.orderId)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isPendingSync ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    disabled={isPendingSync}
                    title="Copy invoice link"
                  >
                    🔗
                  </button>
                  <button
                    onClick={() => deleteOrder(order.orderId)}
                    disabled={isPendingDelete}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isPendingDelete ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`}
                    title="Delete order"
                  >
                    {isPendingDelete ? 'Queued' : '🗑️'}
                  </button>
                </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingOrder && (
        <BillingEditor
          order={editingOrder}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default Billing;